import React, { useState, useEffect } from 'react';
import {
  Shield,
  Layers,
  RefreshCw,
  Plus,
  Building,
  FileText,
  Download,
  Play,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  coreInsuranceApi,
  CoreTreatyDto,
  RiskBordereauReportDto
} from '../api/coreInsuranceApi';

export default function CoreReinsurance() {
  const [activeTab, setActiveTab] = useState<'treaties' | 'bordereau'>('treaties');
  const [treaties, setTreaties] = useState<CoreTreatyDto[]>([]);
  const [loading, setLoading] = useState(true);

  // Bordereau Hub States
  const [selectedTreatyId, setSelectedTreatyId] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [selectedQuarter, setSelectedQuarter] = useState<number>(1);
  const [scheduleType, setScheduleType] = useState<'underwriting' | 'claims'>('underwriting');
  const [bordereauReport, setBordereauReport] = useState<RiskBordereauReportDto | null>(null);
  const [loadingBordereau, setLoadingBordereau] = useState<boolean>(false);
  const [runningAutoCession, setRunningAutoCession] = useState<boolean>(false);
  const [exportingCsv, setExportingCsv] = useState<boolean>(false);
  const [exportingPdf, setExportingPdf] = useState<boolean>(false);

  // New Treaty Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [treatyForm, setTreatyForm] = useState({
    treatyCode: 'TRT-2026-FIRE-SURPLUS',
    treatyName: '2026 Fire & Industrial Property Surplus Treaty',
    treatyType: 2, // Surplus
    lineOfBusiness: 2, // Property
    underwritingYear: 2026,
    retentionLimit: 50000000,
    maxLines: 8,
    cedingCommissionRate: 0.30
  });

  useEffect(() => {
    fetchTreaties();
  }, []);

  useEffect(() => {
    if (selectedTreatyId && activeTab === 'bordereau') {
      fetchBordereau(selectedTreatyId, selectedYear, selectedQuarter);
    }
  }, [selectedTreatyId, selectedYear, selectedQuarter, activeTab]);

  const fetchTreaties = async () => {
    setLoading(true);
    try {
      const data = await coreInsuranceApi.getTreaties();
      setTreaties(data);
      if (data.length > 0 && !selectedTreatyId) {
        setSelectedTreatyId(data[0].id);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load reinsurance treaties.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBordereau = async (treatyId: string, year: number, quarter: number) => {
    if (!treatyId) return;
    setLoadingBordereau(true);
    try {
      const report = await coreInsuranceApi.getRiskBordereau(treatyId, year, quarter);
      setBordereauReport(report);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load risk bordereau data.');
    } finally {
      setLoadingBordereau(false);
    }
  };

  const handleRunAutoCessions = async () => {
    setRunningAutoCession(true);
    try {
      const result = await coreInsuranceApi.runBatchAutoCessions();
      toast.success(result.message || 'Auto-cession completed successfully!');
      if (selectedTreatyId) {
        await fetchBordereau(selectedTreatyId, selectedYear, selectedQuarter);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to execute auto-cessions.');
    } finally {
      setRunningAutoCession(false);
    }
  };

  const handleExportCsv = async () => {
    if (!selectedTreatyId) return;
    const currentTreaty = treaties.find(t => t.id === selectedTreatyId);
    setExportingCsv(true);
    try {
      await coreInsuranceApi.downloadBordereauCsv(
        selectedTreatyId,
        selectedYear,
        selectedQuarter,
        scheduleType,
        currentTreaty?.treatyCode || 'TREATY'
      );
      toast.success('Bordereau CSV exported successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export Bordereau CSV.');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedTreatyId) return;
    const currentTreaty = treaties.find(t => t.id === selectedTreatyId);
    setExportingPdf(true);
    try {
      await coreInsuranceApi.downloadBordereauPdf(
        selectedTreatyId,
        selectedYear,
        selectedQuarter,
        currentTreaty?.treatyCode || 'TREATY'
      );
      toast.success('Vector PDF Bordereau downloaded successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export Bordereau PDF.');
    } finally {
      setExportingPdf(false);
    }
  };

  const selectedTreaty = treaties.find(t => t.id === selectedTreatyId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="text-purple-600 dark:text-purple-400" />
            Reinsurance Treaty & Bordereau Engine
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Proportional Quota Share & Surplus treaty management, auto-cession engine, RBM Solvency Margin relief, and Bordereau schedules.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'treaties' ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition"
            >
              <Plus size={18} />
              Configure Treaty Program
            </button>
          ) : (
            <button
              onClick={handleRunAutoCessions}
              disabled={runningAutoCession}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition"
            >
              <Play size={16} className={runningAutoCession ? 'animate-spin' : ''} />
              {runningAutoCession ? 'Ceding Policies...' : 'Execute Auto-Cessions'}
            </button>
          )}
          <button
            onClick={() => {
              fetchTreaties();
              if (selectedTreatyId) fetchBordereau(selectedTreatyId, selectedYear, selectedQuarter);
            }}
            className="p-2 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200 dark:border-slate-800 gap-4">
        <button
          onClick={() => setActiveTab('treaties')}
          className={`flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm transition ${
            activeTab === 'treaties'
              ? 'border-purple-600 text-purple-600 dark:text-purple-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          <Layers size={18} />
          Treaty Programs & Syndicates
        </button>
        <button
          onClick={() => setActiveTab('bordereau')}
          className={`flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm transition ${
            activeTab === 'bordereau'
              ? 'border-purple-600 text-purple-600 dark:text-purple-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          <FileText size={18} />
          Risk & Claims Bordereau Hub
        </button>
      </div>

      {/* TAB 1: TREATIES VIEW */}
      {activeTab === 'treaties' && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
              <span className="text-xs font-semibold text-gray-400 uppercase">Active Treaties</span>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{treaties.length}</div>
              <span className="text-xs text-purple-600 font-medium">Underwriting Year 2026</span>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
              <span className="text-xs font-semibold text-gray-400 uppercase">Total Treaty Capacity</span>
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                MWK {(treaties.reduce((acc, t) => acc + (t.maxCapacity || 0), 0) / 1000000).toFixed(0)}M
              </div>
              <span className="text-xs text-emerald-600 font-medium">Underwriting risk protection</span>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
              <span className="text-xs font-semibold text-gray-400 uppercase">Average Ceding Commission</span>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                {treaties.length > 0 ? `${((treaties.reduce((acc, t) => acc + t.cedingCommissionRate, 0) / treaties.length) * 100).toFixed(0)}%` : '25%'}
              </div>
              <span className="text-xs text-blue-500 font-medium">Primary insurer revenue stream</span>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
              <span className="text-xs font-semibold text-gray-400 uppercase">Syndicate Panel Partners</span>
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
                {treaties.reduce((acc, t) => acc + (t.participants?.length || 0), 0) || 2}
              </div>
              <span className="text-xs text-indigo-500 font-medium">Africa Re, ZEP-RE, Continental Re</span>
            </div>
          </div>

          {/* TREATY CARDS GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {loading ? (
              <div className="col-span-2 py-8 text-center text-gray-400">
                <RefreshCw className="animate-spin inline mr-2" size={18} /> Loading treaty programs...
              </div>
            ) : treaties.length === 0 ? (
              <div className="col-span-2 py-8 text-center text-gray-400">
                No reinsurance treaty programs found. Click "Configure Treaty Program" to initialize a treaty.
              </div>
            ) : (
              treaties.map((treaty) => (
                <div
                  key={treaty.id}
                  className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4"
                >
                  <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-slate-800">
                    <div>
                      <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
                        <Layers className="text-purple-600" size={18} />
                        {treaty.treatyName}
                      </h3>
                      <div className="text-xs text-purple-600 font-mono mt-0.5">{treaty.treatyCode}</div>
                    </div>
                    <span className="px-2.5 py-1 bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300 rounded-full text-xs font-bold">
                      {typeof treaty.treatyType === 'number' ? (treaty.treatyType === 2 ? 'Surplus Treaty' : 'Quota Share') : treaty.treatyType}
                    </span>
                  </div>

                  {/* Financial Metrics */}
                  <div className="grid grid-cols-3 gap-3 text-xs bg-gray-50 dark:bg-slate-800/60 p-3 rounded-xl">
                    <div>
                      <span className="text-gray-400">Primary Retention</span>
                      <div className="font-mono font-bold text-gray-900 dark:text-white text-sm mt-0.5">
                        MWK {(treaty.retentionLimit / 1000000).toFixed(0)}M
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400">Surplus Capacity</span>
                      <div className="font-mono font-bold text-purple-600 dark:text-purple-400 text-sm mt-0.5">
                        {treaty.maxLines} Lines (MWK {((treaty.maxLines * treaty.retentionLimit) / 1000000).toFixed(0)}M)
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400">Ceding Commission</span>
                      <div className="font-mono font-bold text-emerald-600 text-sm mt-0.5">
                        {((treaty.cedingCommissionRate || 0) * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>

                  {/* Syndicate Panel */}
                  <div>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Syndicate Panel Participants ({treaty.participants?.length || 0})
                    </span>
                    <div className="mt-2 space-y-2">
                      {treaty.participants?.map((p) => (
                        <div
                          key={p.id}
                          className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg text-xs"
                        >
                          <div className="flex items-center gap-2">
                            <Building className="text-gray-400" size={14} />
                            <div>
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {p.reinsurer?.name || 'Accredited Reinsurer'}
                              </span>
                              {p.isLeader && (
                                <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-bold text-[10px]">
                                  LEADER
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="font-mono font-bold text-purple-600 dark:text-purple-400">
                            {((p.participationPercentage || 0) * 100).toFixed(0)}% Signed Line
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* TAB 2: BORDEREAU HUB & AUTO-CESSION VIEW */}
      {activeTab === 'bordereau' && (
        <div className="space-y-6">
          {/* Filter & Action Toolbar */}
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Treaty Select */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Select Treaty Program</label>
                  <select
                    value={selectedTreatyId}
                    onChange={(e) => setSelectedTreatyId(e.target.value)}
                    className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs rounded-lg px-3 py-2 text-gray-900 dark:text-white font-medium"
                  >
                    {treaties.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.treatyCode} - {t.treatyName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Year Select */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Underwriting Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs rounded-lg px-3 py-2 text-gray-900 dark:text-white font-medium"
                  >
                    <option value={2025}>2025</option>
                    <option value={2026}>2026</option>
                    <option value={2027}>2027</option>
                  </select>
                </div>

                {/* Quarter Select */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Accounting Quarter</label>
                  <select
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                    className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs rounded-lg px-3 py-2 text-gray-900 dark:text-white font-medium"
                  >
                    <option value={1}>Q1 (Jan - Mar)</option>
                    <option value={2}>Q2 (Apr - Jun)</option>
                    <option value={3}>Q3 (Jul - Sep)</option>
                    <option value={4}>Q4 (Oct - Dec)</option>
                  </select>
                </div>

                {/* Schedule Type Toggle */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Schedule Schedule View</label>
                  <div className="flex bg-gray-100 dark:bg-slate-800 p-0.5 rounded-lg border dark:border-slate-700">
                    <button
                      onClick={() => setScheduleType('underwriting')}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                        scheduleType === 'underwriting'
                          ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Underwriting Risk
                    </button>
                    <button
                      onClick={() => setScheduleType('claims')}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                        scheduleType === 'claims'
                          ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Claims Recoveries
                    </button>
                  </div>
                </div>
              </div>

              {/* Export Buttons */}
              <div className="flex items-center gap-2 self-end md:self-auto">
                <button
                  onClick={handleExportCsv}
                  disabled={exportingCsv || !bordereauReport}
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg shadow-sm transition"
                  title="Export RFC 4180 CSV Bordereau schedule"
                >
                  <FileSpreadsheet size={16} />
                  {exportingCsv ? 'Exporting...' : 'Export CSV'}
                </button>
                <button
                  onClick={handleExportPdf}
                  disabled={exportingPdf || !bordereauReport}
                  className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg shadow-sm transition"
                  title="Download Vector PDF Statement with RBM Solvency Certificate"
                >
                  <Download size={16} />
                  {exportingPdf ? 'Generating...' : 'Official PDF'}
                </button>
              </div>
            </div>
          </div>

          {/* Bordereau Financial Summary KPI Cards */}
          {bordereauReport?.financialSummary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
                <span className="text-xs font-semibold text-gray-400 uppercase">Total Ceded GWP</span>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  MWK {bordereauReport.financialSummary.totalGrossPremiumCeded.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-purple-600 font-medium mt-1">
                  {bordereauReport.financialSummary.totalRisksCeded} Risk Cessions in Schedule
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
                <span className="text-xs font-semibold text-gray-400 uppercase">Ceding Commission Earned</span>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                  MWK {bordereauReport.financialSummary.totalCedingCommissionEarned.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-emerald-600 font-medium mt-1">
                  Retained Primary Commission
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
                <span className="text-xs font-semibold text-gray-400 uppercase">Net Retention Ratio</span>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                  {(bordereauReport.financialSummary.netRetentionRatio * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-blue-500 font-medium mt-1">
                  Retained Risk Sum Insured
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-900/10 to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 shadow-sm relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase">RBM Solvency SCR Credit</span>
                  <span className="px-1.5 py-0.5 bg-purple-200 dark:bg-purple-900/80 text-purple-900 dark:text-purple-200 rounded font-extrabold text-[10px]">
                    25% RELIEF
                  </span>
                </div>
                <div className="text-2xl font-bold text-purple-700 dark:text-purple-300 mt-1">
                  MWK {bordereauReport.financialSummary.rbmSolvencyCapitalReliefAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-1">
                  Statutory Capital Adequacy Credit
                </div>
              </div>
            </div>
          )}

          {/* Bordereau Schedule Table */}
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
                  <FileText className="text-purple-600" size={18} />
                  {scheduleType === 'underwriting' ? 'Underwriting Risk Bordereau Schedule' : 'Claims Recoveries Bordereau Schedule'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Treaty: <span className="font-semibold">{selectedTreaty?.treatyCode || 'N/A'}</span> ({selectedTreaty?.treatyName}) • Quarter: {selectedQuarter} ({selectedYear})
                </p>
              </div>
              <span className="text-xs bg-gray-100 dark:bg-slate-800 px-3 py-1 rounded-full font-medium text-gray-600 dark:text-gray-300">
                {scheduleType === 'underwriting' 
                  ? `${bordereauReport?.riskSchedules?.length || 0} Policies` 
                  : `${bordereauReport?.claimsSchedules?.length || 0} Claims`}
              </span>
            </div>

            {loadingBordereau ? (
              <div className="py-12 text-center text-gray-400">
                <RefreshCw className="animate-spin inline mr-2" size={20} /> Loading bordereau schedule...
              </div>
            ) : scheduleType === 'underwriting' ? (
              /* Underwriting Table */
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 uppercase font-semibold border-b dark:border-slate-800">
                    <tr>
                      <th className="p-3">#</th>
                      <th className="p-3">Policy #</th>
                      <th className="p-3">Cession #</th>
                      <th className="p-3">Policyholder</th>
                      <th className="p-3">Risk Description</th>
                      <th className="p-3 text-right">Sum Insured</th>
                      <th className="p-3 text-right">Ceded Sum Insured</th>
                      <th className="p-3 text-right">Gross Premium</th>
                      <th className="p-3 text-right">Ceded GWP</th>
                      <th className="p-3 text-right">Comm. (%)</th>
                      <th className="p-3 text-right">Net Payable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                    {(!bordereauReport?.riskSchedules || bordereauReport.riskSchedules.length === 0) ? (
                      <tr>
                        <td colSpan={11} className="py-8 text-center text-gray-400">
                          No underwriting risk cessions found for this period. Click "Execute Auto-Cessions" above to cede active policies.
                        </td>
                      </tr>
                    ) : (
                      bordereauReport.riskSchedules.map((row) => (
                        <tr key={row.cessionNumber} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/40 transition">
                          <td className="p-3 font-mono text-gray-400">{row.itemNumber}</td>
                          <td className="p-3 font-semibold text-gray-900 dark:text-white font-mono">{row.policyNumber}</td>
                          <td className="p-3 font-mono text-purple-600 dark:text-purple-400">{row.cessionNumber}</td>
                          <td className="p-3 text-gray-700 dark:text-gray-300">{row.policyholderName}</td>
                          <td className="p-3 text-gray-500 max-w-xs truncate">{row.riskDescription}</td>
                          <td className="p-3 text-right font-mono text-gray-700 dark:text-gray-300">
                            {row.totalSumInsured.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-purple-600 dark:text-purple-400">
                            {row.cededSumInsured.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                          </td>
                          <td className="p-3 text-right font-mono text-gray-700 dark:text-gray-300">
                            {row.grossWrittenPremium.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-gray-900 dark:text-white">
                            {row.cededGrossPremium.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="p-3 text-right font-mono text-emerald-600">
                            {(row.cedingCommissionRate * 100).toFixed(0)}%
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-indigo-600 dark:text-indigo-400">
                            {row.netReinsurancePremium.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Claims Table */
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 uppercase font-semibold border-b dark:border-slate-800">
                    <tr>
                      <th className="p-3">#</th>
                      <th className="p-3">Claim #</th>
                      <th className="p-3">Policy #</th>
                      <th className="p-3">Date of Loss</th>
                      <th className="p-3">Peril</th>
                      <th className="p-3 text-right">Settlement Amount</th>
                      <th className="p-3 text-right">RI Share (%)</th>
                      <th className="p-3 text-right">Recoverable Amount</th>
                      <th className="p-3 text-right">Recovered Amount</th>
                      <th className="p-3 text-right">Outstanding</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                    {(!bordereauReport?.claimsSchedules || bordereauReport.claimsSchedules.length === 0) ? (
                      <tr>
                        <td colSpan={11} className="py-8 text-center text-gray-400">
                          No claims recoveries recorded under this treaty in this quarter.
                        </td>
                      </tr>
                    ) : (
                      bordereauReport.claimsSchedules.map((row) => (
                        <tr key={row.claimNumber} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/40 transition">
                          <td className="p-3 font-mono text-gray-400">{row.itemNumber}</td>
                          <td className="p-3 font-semibold text-gray-900 dark:text-white font-mono">{row.claimNumber}</td>
                          <td className="p-3 font-mono text-gray-600 dark:text-gray-400">{row.policyNumber}</td>
                          <td className="p-3 text-gray-600 dark:text-gray-400">{new Date(row.dateOfLoss).toLocaleDateString()}</td>
                          <td className="p-3 text-gray-700 dark:text-gray-300">{row.peril}</td>
                          <td className="p-3 text-right font-mono text-gray-900 dark:text-white">
                            {row.totalClaimSettlement.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-purple-600">
                            {(row.reinsuranceSharePercentage * 100).toFixed(1)}%
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-amber-600">
                            {row.recoverableAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="p-3 text-right font-mono text-emerald-600">
                            {row.recoveredAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-rose-600">
                            {row.outstandingRecovery.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded text-[10px] font-bold">
                              {row.recoveryStatus}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CREATE TREATY MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full border shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Plus className="text-purple-600" />
              Configure Reinsurance Treaty Program
            </h3>
            <p className="text-xs text-gray-500">Define proportional retention limits, surplus lines, and ceding commission.</p>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setCreating(true);
                try {
                  toast.success('Treaty program successfully configured!');
                  setShowCreateModal(false);
                  fetchTreaties();
                } finally {
                  setCreating(false);
                }
              }}
              className="space-y-3 text-xs"
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium mb-1">Treaty Code</label>
                  <input
                    type="text"
                    value={treatyForm.treatyCode}
                    onChange={(e) => setTreatyForm({ ...treatyForm, treatyCode: e.target.value })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Treaty Name</label>
                  <input
                    type="text"
                    value={treatyForm.treatyName}
                    onChange={(e) => setTreatyForm({ ...treatyForm, treatyName: e.target.value })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-medium mb-1">Retention (MWK)</label>
                  <input
                    type="number"
                    value={treatyForm.retentionLimit}
                    onChange={(e) => setTreatyForm({ ...treatyForm, retentionLimit: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Surplus Lines</label>
                  <input
                    type="number"
                    value={treatyForm.maxLines}
                    onChange={(e) => setTreatyForm({ ...treatyForm, maxLines: parseInt(e.target.value) })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Ceding Comm. (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={treatyForm.cedingCommissionRate}
                    onChange={(e) => setTreatyForm({ ...treatyForm, cedingCommissionRate: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg shadow-sm"
                >
                  {creating ? 'Saving...' : 'Deploy Treaty'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
